import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, createSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";

export type RoomRow = {
  roomNumber: string;
  roomName: string;
  doctorsCount: number;
};

function mapRoomDoc(d: {
  roomNumber?: string; roomName?: string; doctorsCount?: number;
}): RoomRow {
  return {
    roomNumber: String(d.roomNumber ?? ""),
    roomName: String(d.roomName ?? ""),
    doctorsCount: Number.isFinite(Number(d.doctorsCount))
      ? Number(d.doctorsCount) : 0,
  };
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { message?: string; error?: string | { message?: string } } };
  };
  const d = ax?.response?.data;
  if (typeof d?.message === "string" && d.message.trim()) return d.message.trim();
  const e = d?.error;
  if (typeof e === "string" && e.trim()) return e.trim();
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: string }).message;
    if (typeof m === "string" && m.trim()) return m.trim();
  }
  return fallback;
}

export default function ManageRoomTab() {
  const [rows, setRows] = useState<RoomRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddRoomOpen, setIsAddRoomOpen] = useState(false);
  const [newRoom, setNewRoom] = useState<RoomRow>({
    roomNumber: "",
    roomName: "",
    doctorsCount: 0,
  });
  const [roomNumberError, setRoomNumberError] = useState<string | null>(null);
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(() => new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);

  const fetchRooms = useCallback(() => {
    setLoading(true);
    baseUrl
      .get("/client/setting/clinicRoom/getRooms")
      .then((res) => {
        const list = res.data?.data ?? [];
        setRows(Array.isArray(list) ? list.map(mapRoomDoc) : []);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to load rooms"
        );
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    const valid = new Set(rows.map((r) => r.roomNumber));
    setSelectedRoomIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
        else changed = true;
      });
      if (next.size !== prev.size) changed = true;
      return changed ? next : prev;
    });
  }, [rows]);

  const allRoomsSelected = rows.length > 0 && rows.every((r) => selectedRoomIds.has(r.roomNumber));
  const someRoomsSelected = rows.some((r) => selectedRoomIds.has(r.roomNumber));

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = someRoomsSelected && !allRoomsSelected;
  }, [someRoomsSelected, allRoomsSelected]);

  const toggleRoomSelected = (roomNumber: string) => {
    setSelectedRoomIds((prev) => {
      const next = new Set(prev);
      if (next.has(roomNumber)) next.delete(roomNumber);
      else next.add(roomNumber);
      return next;
    });
  };

  const toggleSelectAllRooms = () => {
    setSelectedRoomIds((prev) => {
      if (rows.length === 0) return new Set();
      if (rows.every((r) => prev.has(r.roomNumber))) return new Set();
      return new Set(rows.map((r) => r.roomNumber));
    });
  };

  const customRoomsSearch =
    selectedRoomIds.size > 0
      ? `?${createSearchParams({
        rooms: Array.from(selectedRoomIds).sort().join(","),
      }).toString()}`
      : "";

  const closeAddRoom = () => {
    setIsAddRoomOpen(false);
    setRoomNumberError(null);
    setNewRoom({
      roomNumber: "",
      roomName: "",
      doctorsCount: 0,
    });
  };

  const onSaveRoom = (e: React.FormEvent) => {
    e.preventDefault();
    setRoomNumberError(null);

    const trimmed = {
      roomNumber: newRoom.roomNumber.trim(),
      roomName: newRoom.roomName.trim(),
      doctorsCount: Number.isFinite(newRoom.doctorsCount) ? newRoom.doctorsCount : 0,
    };

    if (!trimmed.roomNumber || !trimmed.roomName) return;

    const duplicateLocal = rows.some(
      (r) => r.roomNumber === trimmed.roomNumber
    );
    if (duplicateLocal) {
      const msg =
        "This room number is already in use. Choose a different number or delete the existing room first.";
      setRoomNumberError(msg);
      toast.error(msg);
      return;
    }

    baseUrl
      .post("/client/setting/clinicRoom/upsertRoom", {
        ...trimmed,
        replaceExisting: false,
      })
      .then((res) => {
        if (Array.isArray(res.data?.rooms)) {
          setRows(res.data.rooms.map(mapRoomDoc));
        } else {
          fetchRooms();
        }
        toast.success(res.data?.message || "Room saved");
        closeAddRoom();
      })
      .catch((err) => {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        const msg = getApiErrorMessage(err, "Failed to save room");
        if (status === 409) {
          setRoomNumberError(msg);
        }
        toast.error(msg);
      });
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sm:mx-auto sm:max-w-6xl">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-800">Manage Room</h3>
        <button
          type="button"
          onClick={() => {
            setRoomNumberError(null);
            setNewRoom({
              roomNumber: "",
              roomName: "",
              doctorsCount: 0,
            });
            setIsAddRoomOpen(true);
          }}
          className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
        >
          Add Room
        </button>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            Loading rooms…
          </div>
        ) : (
          <table className="w-full text-center">
            <thead>
              <tr className="bg-slate-50/80">
                <th className="px-2 py-2 w-12">
                  <span className="sr-only">Select for multi-room display</span>
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    checked={allRoomsSelected}
                    onChange={toggleSelectAllRooms}
                    disabled={rows.length === 0}
                    aria-label="Select all rooms"
                  />
                </th>
                <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[120px]">
                  Room Number
                </th>
                <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[140px]">
                  Room Name
                </th>

                <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[140px]">
                  No of doctors
                </th>
                <th className="px-2 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider min-w-[120px]">
                  Queue display
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-sm text-slate-500 text-center"
                  >
                    No rooms yet. Add a room to get started.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.roomNumber} className="hover:bg-slate-50/50">
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                        checked={selectedRoomIds.has(r.roomNumber)}
                        onChange={() => toggleRoomSelected(r.roomNumber)}
                        aria-label={`Select room ${r.roomNumber}`}
                      />
                    </td>
                    <td className="px-2 py-2 text-sm font-medium text-slate-700">
                      {r.roomNumber}
                    </td>
                    <td className="px-2 py-2 text-sm text-slate-600">{r.roomName}</td>
                    <td className="px-2 py-2 text-sm font-medium text-slate-700">
                      {r.doctorsCount}
                    </td>
                    <td className="px-2 py-2 text-sm">
                      <Link
                        to={`/room/${encodeURIComponent(r.roomNumber)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-primary hover:underline"
                      >
                        Open Screen
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && rows.length > 0 ? (
        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50/60">
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-800">{selectedRoomIds.size}</span>{" "}
            room{selectedRoomIds.size === 1 ? "" : "s"} selected for multi display
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedRoomIds(new Set())}
              disabled={selectedRoomIds.size === 0}
              className="px-3 py-1.5 rounded-md text-sm font-semibold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              Clear selection
            </button>
            {selectedRoomIds.size > 0 ? (
              <Link
                to={{ pathname: "/room/custom", search: customRoomsSearch }}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
              >
                Open Room
              </Link>
            ) : (
              <span
                className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-200 text-slate-500 cursor-not-allowed"
                aria-disabled="true"
              >
                Open Room
              </span>
            )}
          </div>
        </div>
      ) : null}

      {isAddRoomOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeAddRoom}
            aria-label="Close modal"
          />
          <div className="relative w-full max-w-sm sm:max-w-md">
            <form
              onSubmit={onSaveRoom}
              className="rounded-xl bg-white shadow-lg border border-slate-200 overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">Add Room</div>
                <button
                  type="button"
                  onClick={closeAddRoom}
                  aria-label="Close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="space-y-1">
                    <div className="text-xs font-semibold text-slate-600">
                      Room Number
                    </div>
                    <input
                      value={newRoom.roomNumber}
                      onChange={(e) => {
                        setRoomNumberError(null);
                        setNewRoom((p) => ({ ...p, roomNumber: e.target.value }));
                      }}
                      className={[
                        "w-full px-3 py-2 rounded-md border outline-none focus:border-primary",
                        roomNumberError
                          ? "border-red-400 focus:border-red-500"
                          : "border-slate-200",
                      ].join(" ")}
                      placeholder="e.g. 7"
                      required
                      aria-invalid={!!roomNumberError}
                      aria-describedby={
                        roomNumberError ? "manage-room-number-error" : undefined
                      }
                    />
                    {roomNumberError ? (
                      <p
                        id="manage-room-number-error"
                        className="text-xs text-red-600 pt-0.5"
                        role="alert"
                      >
                        {roomNumberError}
                      </p>
                    ) : null}
                  </label>
                  <label className="space-y-1">
                    <div className="text-xs font-semibold text-slate-600">
                      No of doctors
                    </div>
                    <input
                      type="number"
                      min={0}
                      value={newRoom.doctorsCount}
                      onChange={(e) =>
                        setNewRoom((p) => ({
                          ...p,
                          doctorsCount: Number(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 rounded-md border border-slate-200 outline-none focus:border-primary"
                      placeholder="e.g. 2"
                    />
                  </label>
                </div>
                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    Room Name
                  </div>
                  <input
                    value={newRoom.roomName}
                    onChange={(e) =>
                      setNewRoom((p) => ({ ...p, roomName: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-md border border-slate-200 outline-none focus:border-primary"
                    placeholder="e.g. Morning clinic"
                    required
                  />
                </label>
              </div>
              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeAddRoom}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
